import React, { useState } from 'react';
import { ArrowRight, Calendar, User, Tag, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useData } from '../context/DataContext';

const BlogPage = () => {
  const { data, loading } = useData();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);

  if (loading || !data || !data.blogData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const blogPosts = data.blogData;
  const postsPerPage = 9;
  const totalPages = Math.ceil(blogPosts.length / postsPerPage);
  const paginatedPosts = blogPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      {/* Hero Section */}
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">Insights</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Blog
            </h1>
            <p className="hide text-xl text-slate-300 mb-8 leading-relaxed">
              Insights on fundraising, M&A, strategy, and the founder journey from the Samvedanam team
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            {paginatedPosts.map((post) => (
              <Card key={post.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all group overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant="outline" className="border-amber-500/30 text-amber-500">
                      <Tag className="h-3 w-3 mr-1" />
                      {post.category}
                    </Badge>
                    <span className="text-slate-400 text-sm flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {post.date}
                    </span>
                  </div>
                  <CardTitle className="text-xl text-white mb-3 group-hover:text-amber-500 transition-colors">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="text-slate-400 leading-relaxed">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-slate-400 text-sm">
                      <User className="h-4 w-4 mr-2" />
                      {post.author}
                    </div>
                    <Button
                      variant="ghost"
                      className="text-amber-500 hover:text-amber-400 hover:bg-slate-700"
                      onClick={() => setSelectedPost(post)}
                    >
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant="outline"
                    className={`border-slate-600 text-slate-300 ${currentPage === page ? 'bg-amber-600/20 text-amber-500 border-amber-500/50' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Blog Post Modal */}
      <Dialog open={!!selectedPost} onOpenChange={(open) => !open && setSelectedPost(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="outline" className="border-amber-500/30 text-amber-500">
                    <Tag className="h-3 w-3 mr-1" />
                    {selectedPost.category}
                  </Badge>
                  <span className="text-slate-400 text-sm flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {selectedPost.date}
                  </span>
                </div>
                <DialogTitle className="text-2xl md:text-3xl text-white mb-4">
                  {selectedPost.title}
                </DialogTitle>
                <div className="flex items-center text-slate-400 text-sm mb-6">
                  <User className="h-4 w-4 mr-2" />
                  {selectedPost.author}
                </div>
              </DialogHeader>

              {selectedPost.image && (
                <div className="w-full h-64 md:h-80 overflow-hidden rounded-lg mb-6">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="prose prose-invert max-w-none">
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {selectedPost.content || selectedPost.excerpt}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default BlogPage;
